/* gr-search.js — Predictive search for GothRider header modal.
 *
 * Uses Shopify's native /search/suggest.json endpoint with resource hints for
 * queries, products, and collections. Handles:
 *   - 200ms debounce on keystrokes
 *   - request cancellation when a newer query comes in
 *   - empty / idle / loading / results state toggling
 *   - keyboard navigation: ↑ ↓ Enter, wraps, Esc closes
 *   - aria-activedescendant for a11y
 *   - clear button + query persistence across open/close
 */
(function () {
  'use strict';

  var modal = document.getElementById('gr-search-modal');
  if (!modal) return;

  var form      = modal.querySelector('[data-gr-search-form]');
  var input     = modal.querySelector('[data-gr-search-input]');
  var clearBtn  = modal.querySelector('[data-gr-search-clear]');
  var body      = modal.querySelector('[data-gr-search-body]');
  var seeAll    = modal.querySelector('[data-gr-search-see-all]');
  var emptyQ    = modal.querySelector('[data-gr-search-empty-q]');

  var states = {
    idle:    body.querySelector('[data-gr-search-state="idle"]'),
    loading: body.querySelector('[data-gr-search-state="loading"]'),
    results: body.querySelector('[data-gr-search-state="results"]'),
    empty:   body.querySelector('[data-gr-search-state="empty"]')
  };

  var lists = {
    queries:     body.querySelector('[data-gr-search-list="queries"]'),
    collections: body.querySelector('[data-gr-search-list="collections"]'),
    products:    body.querySelector('[data-gr-search-list="products"]')
  };

  var tmpls = {
    query:      modal.querySelector('[data-gr-search-tmpl="query"]'),
    collection: modal.querySelector('[data-gr-search-tmpl="collection"]'),
    product:    modal.querySelector('[data-gr-search-tmpl="product"]')
  };

  var abortCtl = null;
  var timer = null;
  var cache = {};
  var rowIndex = -1;
  var rows = [];
  var SUGGEST_URL = (window.Shopify && Shopify.routes && Shopify.routes.root
                      ? Shopify.routes.root : '/') + 'search/suggest.json';

  // ── State ─────────────────────────────────────────────
  function setState(name) {
    Object.keys(states).forEach(function (k) {
      if (!states[k]) return;
      states[k].hidden = (k !== name);
    });
    input.setAttribute('aria-expanded', name === 'results' || name === 'idle' ? 'true' : 'false');
  }

  function money(price) {
    var n = parseFloat(price) || 0;
    // Shopify suggest API returns price as dollars (e.g. "24.00"), not cents
    var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency }).format(n);
    } catch (e) {
      return '$' + n.toFixed(2);
    }
  }

  function renderRow(kind, item) {
    var node = tmpls[kind].content.firstElementChild.cloneNode(true);
    var a = node.querySelector('[data-href]');

    if (kind === 'query') {
      a.href = item.url || ('/search?q=' + encodeURIComponent(item.text));
      node.querySelector('[data-text]').textContent = item.text;
    } else if (kind === 'collection') {
      a.href = item.url;
      node.querySelector('[data-text]').textContent = item.title;
      if (item.products_count != null) {
        node.querySelector('[data-count]').textContent = item.products_count;
      }
    } else if (kind === 'product') {
      a.href = item.url;
      var img = node.querySelector('[data-img]');
      if (item.featured_image && item.featured_image.url) {
        img.src = item.featured_image.url;
        img.alt = item.title;
      } else if (item.image) {
        img.src = item.image;
        img.alt = item.title;
      } else {
        img.remove();
      }
      node.querySelector('[data-title]').textContent = item.title;
      var sub = node.querySelector('[data-sub]');
      sub.textContent = item.vendor || (item.product_type || '');
      node.querySelector('[data-price]').textContent = money(item.price);
    }
    return node;
  }

  function clearLists() {
    Object.values(lists).forEach(function (l) { if (l) l.innerHTML = ''; });
  }

  function render(data, q) {
    clearLists();
    var r = (data && data.resources && data.resources.results) || {};
    var qs = r.queries     || [];
    var cs = r.collections || [];
    var ps = r.products    || [];

    if (!qs.length && !cs.length && !ps.length) {
      if (emptyQ) emptyQ.textContent = q;
      setState('empty');
      return;
    }

    qs.slice(0, 6).forEach(function (q) { lists.queries.appendChild(renderRow('query', q)); });
    cs.slice(0, 4).forEach(function (c) { lists.collections.appendChild(renderRow('collection', c)); });
    ps.slice(0, 6).forEach(function (p) { lists.products.appendChild(renderRow('product', p)); });

    // Hide empty columns
    body.querySelector('[data-gr-search-col="queries"]').style.display     = qs.length ? '' : 'none';
    body.querySelector('[data-gr-search-col="collections"]').style.display = cs.length ? '' : 'none';

    if (seeAll) {
      var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      seeAll.href = root + 'search?q=' + encodeURIComponent(q);
    }

    setState('results');
    indexRows();
  }

  // ── Fetch ─────────────────────────────────────────────
  function suggest(q) {
    if (cache[q]) { render(cache[q], q); return; }

    if (abortCtl) abortCtl.abort();
    abortCtl = (typeof AbortController !== 'undefined') ? new AbortController() : null;

    setState('loading');

    var url = SUGGEST_URL
      + '?q=' + encodeURIComponent(q)
      + '&resources[type]=query,product,collection'
      + '&resources[limit]=6'
      + '&resources[options][unavailable_products]=last'
      + '&resources[options][fields]=title,vendor,product_type,body';

    fetch(url, {
      headers: { 'Accept': 'application/json' },
      signal: abortCtl ? abortCtl.signal : undefined
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        cache[q] = data;
        // Only render if the input still matches this query (race guard)
        if (input.value.trim() === q) render(data, q);
      })
      .catch(function (e) {
        if (e.name === 'AbortError') return;
        // Silent fail — keep idle
        setState('idle');
      });
  }

  // ── Debounce input ────────────────────────────────────
  function onInput() {
    var q = input.value.trim();
    if (clearBtn) clearBtn.hidden = q.length === 0;

    clearTimeout(timer);
    if (!q) { setState('idle'); return; }
    if (q.length < 2) { setState('idle'); return; }

    timer = setTimeout(function () { suggest(q); }, 200);
  }

  // ── Keyboard nav ──────────────────────────────────────
  function indexRows() {
    rows = Array.prototype.slice.call(
      body.querySelectorAll('.gr-search__row')
    );
    rows.forEach(function (r, i) {
      r.id = 'gr-search-row-' + i;
      r.setAttribute('role', 'option');
    });
    rowIndex = -1;
  }

  function highlight(i) {
    rows.forEach(function (r) { r.classList.remove('is-active'); });
    if (i < 0 || i >= rows.length) { input.removeAttribute('aria-activedescendant'); return; }
    var r = rows[i];
    r.classList.add('is-active');
    input.setAttribute('aria-activedescendant', r.id);
    // Scroll into view (without scrollIntoView to avoid host-breaking)
    var parent = body;
    var rect = r.getBoundingClientRect();
    var prect = parent.getBoundingClientRect();
    if (rect.top < prect.top) parent.scrollTop -= (prect.top - rect.top) + 8;
    else if (rect.bottom > prect.bottom) parent.scrollTop += (rect.bottom - prect.bottom) + 8;
  }

  function onKey(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!rows.length) return;
      rowIndex = (rowIndex + 1) % rows.length;
      highlight(rowIndex);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!rows.length) return;
      rowIndex = (rowIndex - 1 + rows.length) % rows.length;
      highlight(rowIndex);
    } else if (e.key === 'Enter') {
      if (rowIndex >= 0 && rows[rowIndex]) {
        e.preventDefault();
        var a = rows[rowIndex].querySelector('a');
        if (a) window.location.href = a.href;
      }
      // else default form submit → /search?q=...
    } else if (e.key === 'Escape') {
      if (input.value) {
        input.value = '';
        onInput();
        if (clearBtn) clearBtn.hidden = true;
      } else {
        modal.setAttribute('aria-hidden', 'true');
      }
    }
  }

  // ── Wire ──────────────────────────────────────────────
  input.addEventListener('input', onInput);
  input.addEventListener('keydown', onKey);

  if (clearBtn) {
    clearBtn.addEventListener('click', function () {
      input.value = '';
      input.focus();
      onInput();
    });
  }

  // When opened, restore previous query if any and re-run
  var observer = new MutationObserver(function () {
    if (modal.getAttribute('aria-hidden') === 'false') {
      setTimeout(function () { input.focus(); }, 60);
      if (input.value.trim().length >= 2) suggest(input.value.trim());
      else setState('idle');
    }
  });
  observer.observe(modal, { attributes: true, attributeFilter: ['aria-hidden'] });

  // Close on row click — defer so browser navigation fires first
  body.addEventListener('click', function (e) {
    var a = e.target.closest('a');
    if (a) {
      setTimeout(function () { modal.setAttribute('aria-hidden', 'true'); }, 50);
    }
  });

  setState('idle');
})();
